import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  createStatusTrigger,
  removeStatusTrigger,
  statusTriggerQuery,
  statusTriggersShellQuery,
  statusTriggersTableQuery,
  updateStatusTrigger,
  type StatusTriggerFields,
  type StatusTriggersShell,
} from '../../api/settingsEaseedeskStatusTriggers'
import { DataTable, useTableState, type TableColumn } from '../../ui/DataTable'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Ticket Status Change Trigger Actions.
 *
 * Element cu element fata de `version-3/easeedesk_settings/tickets/
 * trigger_actions/ticket_status_change_trigger_actions.php` +
 * `edit_action_tab.php`, si coloana cu coloana fata de `TicketTriggerActions`
 * din `public/js/`.
 *
 * O REGULA spune: la o FIRMA, pe un TIP de tichet, cand se intra intr-o STARE,
 * se ruleaza o ACTIUNE (un nume) cu o COMANDA (`action_data`).
 *
 * Lista veche nu lega randurile de furnizorul care se uita - vezi
 * `DbalEaseedeskStatusTriggersSource`. Acum trece prin firmele lui, si tot prin
 * ele trec si scrierile.
 *
 * Clicul se prinde pe invelis, ca la Categories: celulele sunt HTML pus cu
 * `innerHTML`, deci butoanele din ele n-au `onClick` propriu.
 */

const TABLE_ID = 'status-change-trigger-actions'

function esc(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

export function triggerColumns(): TableColumn[] {
  return [
    { index: 0, title: 'ID', orderable: true, visible: true, width: 60, html: (row) => String(Number(row.id)) },
    {
      index: 1,
      title: 'Company',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(String(row.company_name ?? '')),
    },
    {
      index: 2,
      title: 'Ticket type',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(String(row.ticket_type_name ?? '')),
    },
    {
      index: 3,
      title: 'Ticket status',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) =>
        `<span class="label" style="background-color: ${esc(String(row.ticket_status_color ?? ''))}">${esc(
          String(row.ticket_status_name ?? ''),
        )}</span>`,
    },
    {
      index: 4,
      title: 'Action',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(String(row.action ?? '')),
    },
    {
      index: 5,
      title: 'Action data',
      orderable: true,
      visible: true,
      className: 'a-left',
      html: (row) => esc(String(row.action_data ?? '')),
    },
    {
      index: 6,
      title: 'Actions',
      orderable: false,
      visible: true,
      width: 200,
      className: 'center',
      html: (row) =>
        '<div class="btn-group" role="group" aria-label="controls">' +
        `<button type="button" data-trigger="${Number(row.id)}" class="btn btn-sm btn-default edit-trigger">` +
        '<i class="fas fa-pencil-alt" aria-hidden="true"></i> Edit</button>' +
        `<button type="button" data-trigger="${Number(row.id)}" class="btn btn-sm btn-default remove-trigger">` +
        '<i class="fas fa-times" aria-hidden="true"></i> Remove</button>' +
        '</div>',
    },
  ]
}

/** Numele coloanei SQL pentru fiecare indice, ca in harta din model. */
export const ORDER_NAMES = [
  'id',
  'company_name',
  'ticket_type_name',
  'ticket_status_name',
  'action',
  'action_data',
  'id',
]

export function StatusTriggerActionsPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(statusTriggersShellQuery())
  const [state, setState] = useTableState(TABLE_ID, {
    start: 0,
    length: 25,
    order: { column: 0, dir: 'desc' },
    search: '',
  })
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState(0)
  const columns = useMemo(() => triggerColumns(), [])

  const table = useQuery({
    ...statusTriggersTableQuery({
      draw: 1,
      start: state.start,
      length: state.length,
      orderColumn: state.order.column,
      orderName: ORDER_NAMES[state.order.column] ?? 'id',
      orderDir: state.order.dir,
      search: state.search,
    }),
    placeholderData: keepPreviousData,
  })

  const redraw = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'status_triggers'] })
  }

  const remove = useMutation({
    mutationFn: (id: number) => removeStatusTrigger(id),
    onSuccess: () => {
      toast('Resource deleted.', 'Success')
      redraw()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="This screen" />
  }
  if (!shell.data) return <Spinner />

  const rows = (table.data?.data ?? []) as unknown as Record<string, unknown>[]

  const onClick = (event: MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement

    const edit = target.closest('.edit-trigger')
    if (edit instanceof HTMLElement) {
      event.preventDefault()
      setEdited(Number(edit.dataset.trigger ?? 0))
      return
    }

    const kill = target.closest('.remove-trigger')
    if (kill instanceof HTMLElement) {
      event.preventDefault()
      if (!window.confirm('Are you sure?\nYou will not be able to recover this trigger action!')) return
      remove.mutate(Number(kill.dataset.trigger ?? 0))
    }
  }

  return (
    <div onClick={onClick}>
      <button type="button" className="pull-right btn btn-success btn-sm" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Add New Trigger Action
      </button>
      <h4>
        <strong>Ticket Status Change</strong> Trigger Actions
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit <strong>trigger actions</strong>
      </label>
      <hr />

      <DataTable
        id={TABLE_ID}
        columns={columns}
        rows={rows}
        total={table.data?.recordsFiltered ?? 0}
        fetching={table.isFetching}
        error={table.error ? table.error.message : null}
        onRetry={() => void table.refetch()}
        state={state}
        onState={setState}
        rowId={(row) => String(row.DT_RowId ?? `row_${Number(row.id)}`)}
        emptyHint="A trigger action runs a command when a ticket of a given type enters a given status."
      />

      <TriggerModal open={creating} shell={shell.data} onClose={() => setCreating(false)} onSaved={redraw} />
      <TriggerModal
        id={edited}
        open={edited > 0}
        shell={shell.data}
        onClose={() => setEdited(0)}
        onSaved={redraw}
      />
    </div>
  )
}

/**
 * `newResource` si `edit_action_tab` din portal: acelasi formular cu cinci
 * campuri. La EDITARE comanda e obligatorie, ca in `form_validation` din CI3;
 * la creare nu era, si ramane cum a fost.
 */
export function TriggerModal({
  id = 0,
  open,
  shell,
  onClose,
  onSaved,
}: {
  id?: number
  open: boolean
  shell: StatusTriggersShell
  onClose(): void
  onSaved(): void
}) {
  const editing = id > 0
  const details = useQuery(statusTriggerQuery(open && editing ? id : 0))
  const [fields, setFields] = useState<StatusTriggerFields>({
    company: 0,
    ticket_types: 0,
    ticket_status: 0,
    action: '',
    action_data: '',
  })

  useEffect(() => {
    if (!open) return
    if (!editing) {
      setFields({ company: 0, ticket_types: 0, ticket_status: 0, action: '', action_data: '' })
      return
    }
    if (!details.data) return
    setFields({
      company: details.data.company_id,
      ticket_types: details.data.ticket_type_id,
      ticket_status: details.data.ticket_status_id,
      action: details.data.action,
      action_data: details.data.action_data,
    })
  }, [open, editing, details.data])

  const save = useMutation({
    mutationFn: () => (editing ? updateStatusTrigger(id, fields) : createStatusTrigger(fields)),
    onSuccess: () => {
      toast(editing ? 'Resource updated.' : 'Resource created.', 'Success')
      onClose()
      onSaved()
    },
    onError: (error) =>
      toast(error instanceof ApiError && error.message ? error.message : 'Something went wrong.', 'Error', 'error'),
  })

  const incomplete =
    fields.company < 1 ||
    fields.ticket_types < 1 ||
    fields.ticket_status < 1 ||
    fields.action.trim() === '' ||
    (editing && fields.action_data.trim() === '')

  return (
    <Modal
      id={editing ? 'editTriggerAction' : 'newResource'}
      labelledBy={editing ? 'editTriggerActionLabel' : 'newResourceLabel'}
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Trigger Action' : 'Add New Trigger Action'}
      /* modal-lg, ca in macheta veche: in 600px coloana col-md-3 ramanea de
         117px si eticheta "Action Data Command" (139px) iesea din fereastra. */
      large
    >
      {editing && !details.data ? (
        <Spinner />
      ) : (
        <form
          id={editing ? 'edit-action-trigger-form' : 'trigger-action-create'}
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            if (incomplete) return
            save.mutate()
          }}
        >
          <div className="row">
            <div className="col-md-5">
              <div className="form-group">
                <label htmlFor="trigger-company" className="label bg-default color-white" style={{ fontSize: 12 }}>
                  Select Company
                </label>
                <select
                  id="trigger-company"
                  className="form-control"
                  name="company"
                  required
                  value={fields.company}
                  onChange={(e) => setFields((f) => ({ ...f, company: Number(e.target.value) }))}
                >
                  <option value={0}>Select..</option>
                  {shell.companies.map((company) => (
                    <option key={company.ID} value={company.ID}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-4">
              <div className="form-group">
                <label htmlFor="trigger-action" className="label bg-default color-white" style={{ fontSize: 12 }}>
                  Action
                </label>
                <input
                  id="trigger-action"
                  type="text"
                  className="form-control input-sm"
                  name="action"
                  value={fields.action}
                  onChange={(e) => setFields((f) => ({ ...f, action: e.target.value }))}
                />
              </div>
            </div>

            <div className="col-md-3">
              <div className="form-group">
                <label htmlFor="trigger-action-data" className="label bg-default color-white" style={{ fontSize: 12 }}>
                  Action Data Command
                </label>
                <input
                  id="trigger-action-data"
                  type="text"
                  className="form-control input-sm"
                  name="action_data"
                  value={fields.action_data}
                  onChange={(e) => setFields((f) => ({ ...f, action_data: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div className="clearfix"></div>

          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label htmlFor="trigger-types" className="label bg-default color-white" style={{ fontSize: 12 }}>
                  Ticket types
                </label>
                <select
                  id="trigger-types"
                  className="form-control"
                  name="ticket_types"
                  value={fields.ticket_types}
                  onChange={(e) => setFields((f) => ({ ...f, ticket_types: Number(e.target.value) }))}
                >
                  <option value={0}>Select..</option>
                  {shell.ticket_types.map((type) => (
                    <option key={type.ID} value={type.ID}>
                      {type.short_name} ({type.type_name})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-md-6">
              <div className="form-group">
                <label htmlFor="trigger-status" className="label bg-default color-white" style={{ fontSize: 12 }}>
                  Ticket status
                </label>
                <select
                  id="trigger-status"
                  className="form-control"
                  name="ticket_status"
                  value={fields.ticket_status}
                  onChange={(e) => setFields((f) => ({ ...f, ticket_status: Number(e.target.value) }))}
                >
                  <option value={0}>Select..</option>
                  {shell.statuses.map((status) => (
                    <option key={status.ID} value={status.ID}>
                      {status.status_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="form-group mt-2 text-center center-block">
                <button type="button" className="btn btn-default" onClick={onClose}>
                  Close
                </button>{' '}
                <button type="submit" className="btn btn-primary" disabled={incomplete || save.isPending}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </form>
      )}
    </Modal>
  )
}
